import { useCallback, useEffect, useRef } from 'react';
import { apiFetch } from './api';

interface Options {
  articleId: string;
  /** No writes for released ('filed') articles — same contract as scroll progress. */
  status: string;
  /** Min gap between PATCHes while position keeps changing. */
  intervalMs?: number;
}

/**
 * Media counterpart of useScrollProgress: persists the watch point / current
 * page via PATCH { mediaPosition }. `report()` is throttled; `flush()` writes
 * immediately (pause, unmount). The server derives readingProgress from the
 * position, so queue cards stay in sync for free.
 */
export function useMediaProgress({ articleId, status, intervalMs = 5000 }: Options) {
  const lastSentRef = useRef<number>(-1);
  const pendingRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const blockedRef = useRef(status === 'filed');

  useEffect(() => {
    blockedRef.current = status === 'filed';
  }, [status]);

  const send = useCallback(
    (position: number) => {
      if (blockedRef.current) return;
      lastSentRef.current = position;
      pendingRef.current = null;
      apiFetch(`/articles/${articleId}`, {
        method: 'PATCH',
        body: JSON.stringify({ mediaPosition: position }),
      }).catch(() => {});
    },
    [articleId],
  );

  const report = useCallback(
    (position: number) => {
      const p = Math.max(0, Math.floor(position));
      if (p === lastSentRef.current) return;
      pendingRef.current = p;
      if (timerRef.current) return; // a write is already scheduled
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        if (pendingRef.current !== null) send(pendingRef.current);
      }, intervalMs);
    },
    [send, intervalMs],
  );

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = undefined;
    if (pendingRef.current !== null) send(pendingRef.current);
  }, [send]);

  // Trailing write on unmount so the resume point never lags by a whole interval.
  useEffect(() => flush, [flush]);

  return { report, flush };
}
